import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import PlanBadge from '../../components/PlanBadge';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [tab, setTab] = useState('overview');
  const [pendingSubs, setPendingSubs] = useState([]);
  const [subUpdating, setSubUpdating] = useState(null);
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getStats();
      setStats(res.data.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/nz-admin-2847');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSubs = async () => {
    try {
      const res = await adminAPI.getPendingSubscriptions();
      setPendingSubs(res.data.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/nz-admin-2847');
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/nz-admin-2847'); return; }
    fetchStats();
    fetchPendingSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlanChange = async (userId, newPlan) => {
    setUpdating(userId);
    try {
      await adminAPI.updateUserPlan(userId, newPlan);
      await fetchStats();
    } catch (err) { alert('Failed to update plan'); }
    finally { setUpdating(null); }
  };

  const handleApproveSub = async (id) => {
    setSubUpdating(id);
    try {
      await adminAPI.approveSubscription(id);
      await Promise.all([fetchPendingSubs(), fetchStats()]);
    } catch (err) { alert('Failed to approve request'); }
    finally { setSubUpdating(null); }
  };

  const handleRejectSub = async (id) => {
    setSubUpdating(id);
    try {
      await adminAPI.rejectSubscription(id);
      await fetchPendingSubs();
    } catch (err) { alert('Failed to reject request'); }
    finally { setSubUpdating(null); }
  };

  if (loading) return <div style={{ minHeight: '100vh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#666' }}>Loading...</p></div>;

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#fff', fontFamily: 'sans-serif' }}>
      <div style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 18, fontWeight: 700 }}>Nuzlio Admin</h1>
        <button onClick={() => { localStorage.removeItem('adminToken'); navigate('/nz-admin-2847'); }}
          style={{ background: 'none', border: '1px solid #444', color: '#aaa', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Logout</button>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 0' }}>
        {[{ id: 'overview', label: 'Overview' }, { id: 'subscriptions', label: 'Subscriptions' }].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            style={{
              background: tab === id ? '#1a1a1a' : 'none',
              border: '1px solid #333',
              borderBottom: tab === id ? '1px solid #1a1a1a' : '1px solid #333',
              color: tab === id ? '#fff' : '#888',
              padding: '8px 16px',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
            {label}
            {id === 'subscriptions' && pendingSubs.length > 0 && (
              <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{pendingSubs.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
      <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, padding: '24px 24px 0' }}>
        {[{ label: 'Total Users', value: stats?.totalUsers ?? 0, color: '#6C63FF' },
          { label: 'Total Properties', value: stats?.totalProperties ?? 0, color: '#10b981' },
          { label: 'Total Bookings', value: stats?.totalBookings ?? 0, color: '#f59e0b' }].map(c => (
          <div key={c.label} style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 12, padding: 24 }}>
            <p style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>{c.label}</p>
            <p style={{ fontSize: 32, fontWeight: 700, color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 12, padding: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
          <p style={{ color: '#888', fontSize: 13 }}>Users by plan:</p>
          {stats?.byPlan?.map(p => <span key={p.plan}><PlanBadge plan={p.plan} /> <span style={{ color: '#aaa', fontSize: 14 }}>{p.count}</span></span>)}
        </div>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Recent Users</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>{['Name','Email','Properties','Plan','Joined','Change Plan'].map(h => <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#888', fontWeight: 500, borderBottom: '1px solid #333' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {stats?.recentUsers?.map(user => (
                  <tr key={user.id}>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222' }}>{user.full_name}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#aaa' }}>{user.email}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#aaa', textAlign: 'center' }}>{user.property_count || 0}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222' }}><PlanBadge plan={user.plan} /></td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#666', fontSize: 12 }}>{new Date(user.created_at).toLocaleDateString('fr-MA')}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #222' }}>
                      <select value={user.plan} disabled={updating === user.id} onChange={e => handlePlanChange(user.id, e.target.value)}
                        style={{ background: '#111', border: '1px solid #444', color: '#fff', padding: '4px 8px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                        <option value="agency">Agency</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </>
      )}

      {tab === 'subscriptions' && (
        <div style={{ padding: 24 }}>
          <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 12, padding: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Pending Subscription Requests</h2>
            {pendingSubs.length === 0 ? (
              <p style={{ color: '#666', fontSize: 14 }}>No pending requests.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr>{['User','Email','Plan','Amount','Reference','Submitted','Actions'].map(h => <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#888', fontWeight: 500, borderBottom: '1px solid #333' }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {pendingSubs.map(sub => (
                      <tr key={sub.id}>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222' }}>{sub.user_full_name}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#aaa' }}>{sub.user_email}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222' }}><PlanBadge plan={sub.plan} /></td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#aaa' }}>{sub.amount} MAD</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#aaa' }}>{sub.transfer_reference}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#666', fontSize: 12 }}>{new Date(sub.created_at).toLocaleDateString('fr-MA')}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', display: 'flex', gap: 8 }}>
                          <button onClick={() => handleApproveSub(sub.id)} disabled={subUpdating === sub.id}
                            style={{ background: '#22c55e', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: subUpdating === sub.id ? 0.6 : 1 }}>
                            Approve
                          </button>
                          <button onClick={() => handleRejectSub(sub.id)} disabled={subUpdating === sub.id}
                            style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: subUpdating === sub.id ? 0.6 : 1 }}>
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminDashboard;
